import React, { useEffect } from 'react';
import { useProject } from '../../context/ProjectContext';
import { useI18n } from '../../i18n';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { FaPlus } from 'react-icons/fa';
import overviewIcon1 from '../../assets/overview-1.svg';
import overviewIcon2 from '../../assets/overview-2.svg';
import overviewIcon3 from '../../assets/overview-3.svg';
import overviewIcon4 from '../../assets/overview-4.svg';
import ProjectsOverviewSkeleton from './ProjectsOverviewSkeleton';
import './ProjectsOverview.css';

// Status colors from design
const STATUS_COLORS = {
  todo: '#8C8C8C', // To Do - Gray
  in_progress: '#FEBF2F', // In Progress - Yellow
  on_hold: '#F87294', // Hold - Pink
  reopen: '#EF9A62', // Reopened - Orange
  done: '#4EDA8B', // Completed - Green
};

// Custom Legend Component - Fixed Order
const CustomLegend = ({ t }) => {
  const legendItems = [
    { dataKey: 'todo', label: t('legacy.status_todo').toUpperCase(), color: '#8C8C8C' },
    { dataKey: 'in_progress', label: t('legacy.status_in_progress').toUpperCase(), color: '#FEBF2F' },
    { dataKey: 'reopen', label: t('legacy.status_reopen').toUpperCase(), color: '#EF9A62' },
    { dataKey: 'on_hold', label: t('legacy.status_on_hold').toUpperCase(), color: '#F87294' },
    { dataKey: 'done', label: t('legacy.status_done').toUpperCase(), color: '#4EDA8B' },
  ];

  return (
    <div className="custom-legend-wrapper">
      {legendItems.map((item) => (
        <div key={item.dataKey} className="legend-item">
          <div
            className="legend-color-box"
            style={{ backgroundColor: item.color }}
          />
          <span className="legend-label">{item.label}</span>
        </div>
      ))}
    </div>
  );
};

// Modern Tooltip Component
const ModernTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const statusLabels = {
      todo: 'To Do',
      in_progress: 'In Progress',
      on_hold: 'Hold',
      reopen: 'Reopened',
      done: 'Completed',
    };

    // Sort payload in the correct order
    const statusOrder = ['todo', 'in_progress', 'reopen', 'on_hold', 'done'];
    const sortedPayload = [...payload].sort((a, b) => {
      return statusOrder.indexOf(a.dataKey) - statusOrder.indexOf(b.dataKey);
    });

    return (
      <div className="modern-tooltip">
        <p className="tooltip-title">{label}</p>
        <div className="tooltip-entries">
          {sortedPayload.map((entry, index) => (
            <div
              key={index}
              className="tooltip-entry"
              style={{
                borderBottom:
                  index < sortedPayload.length - 1
                    ? '1px solid #f0f0f0'
                    : 'none',
              }}
            >
              <div
                className="tooltip-color-dot"
                style={{
                  backgroundColor: entry.color,
                  boxShadow: `0 2px 8px ${entry.color}40`,
                }}
              />
              <span className="tooltip-status-label">
                {statusLabels[entry.dataKey] || entry.name}
              </span>
              <span className="tooltip-value" style={{ color: entry.color }}>
                {entry.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const ProjectsOverview = ({ onCreateProject }) => {
  const { fetchDashboardStats, dashboardStats, isLoadingDashboard, projects } =
    useProject();
  const { t } = useI18n();

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // Show skeleton while loading dashboard stats
  if (isLoadingDashboard && !dashboardStats) {
    return <ProjectsOverviewSkeleton />;
  }

  // Show empty state when no projects
  if (projects.length === 0) {
    return (
      <div className="projects-overview projects-empty-view">
        <div className="projects-empty-content">
          <div className="projects-empty-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <h2>{t('legacy.welcome_to_projects')}</h2>
          <p>{t('legacy.projects_empty_message')}</p>
          <button className="projects-create-cta" onClick={onCreateProject}>
            <FaPlus size={16} />
            <span>{t('legacy.create_first_project')}</span>
          </button>
        </div>
      </div>
    );
  }

  // Data for charts
  const chartData = dashboardStats?.chart || [];

  // Format chart data for stacked BarChart
  // Transform to include status counts per project
  const formattedData = chartData.map((item) => ({
    name:
      item.project_title.length > 20
        ? item.project_title.substring(0, 20) + '...'
        : item.project_title,
    fullTitle: item.project_title,
    todo: parseInt(item.todo) || 0,
    in_progress: parseInt(item.in_progress) || 0,
    on_hold: parseInt(item.on_hold) || 0,
    reopen: parseInt(item.reopen) || 0,
    done: parseInt(item.done) || 0,
  }));

  const stats = dashboardStats?.overview || {
    completed: 0,
    in_progress: 0,
    overdue: 0,
    on_hold: 0,
  };

  return (
    <div className="projects-overview">
      <h2>{t('legacy.projects_overview')}</h2>

      <h3 className="overview-section-title">{t('legacy.tasks_overview')}</h3>
      <div className="stats-grid">
        <div className="stat-card completed">
          <div className="stat-content">
            <h3>{t('legacy.completed_task')}</h3>
            <div className="stat-value">{stats.completed}</div>
          </div>
          <div className="stat-icon">
            <img src={overviewIcon1} alt="Completed Task" />
          </div>
        </div>

        <div className="stat-card progress">
          <div className="stat-content">
            <h3>{t('legacy.task_in_progress')}</h3>
            <div className="stat-value">{stats.in_progress}</div>
          </div>
          <div className="stat-icon">
            <img src={overviewIcon2} alt="Task in progress" />
          </div>
        </div>

        <div className="stat-card overdue">
          <div className="stat-content">
            <h3>{t('legacy.overdue_task')}</h3>
            <div className="stat-value">{stats.overdue}</div>
          </div>
          <div className="stat-icon">
            <img src={overviewIcon3} alt="Overdue Task" />
          </div>
        </div>

        <div className="stat-card hold">
          <div className="stat-content">
            <h3>{t('legacy.task_on_hold')}</h3>
            <div className="stat-value">{stats.on_hold}</div>
          </div>
          <div className="stat-icon">
            <img src={overviewIcon4} alt="Task on Hold" />
          </div>
        </div>
      </div>

      <h3 className="overview-section-title">
        {t('legacy.task_status_by_project')}
      </h3>
      <div className="charts-section">
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={formattedData}
              margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
              barSize={window.innerWidth < 768 ? 30 : 80}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                type="category"
                tick={{ fill: '#64748b', className: 'responsive-chart-tick' }}
                axisLine={false}
                tickLine={false}
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
              />
              <YAxis
                type="number"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748b', className: 'responsive-chart-tick' }}
              />
              <Tooltip
                content={<ModernTooltip />}
                cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
              />
              <Legend content={<CustomLegend t={t} />} />
              <Bar
                dataKey="todo"
                stackId="status"
                fill={STATUS_COLORS.todo}
                name="TO DO"
              />
              <Bar
                dataKey="in_progress"
                stackId="status"
                fill={STATUS_COLORS.in_progress}
                name="IN PROGRESS"
              />
              <Bar
                dataKey="reopen"
                stackId="status"
                fill={STATUS_COLORS.reopen}
                name="REOPENED"
              />
              <Bar
                dataKey="on_hold"
                stackId="status"
                fill={STATUS_COLORS.on_hold}
                name="HOLD"
              />
              <Bar
                dataKey="done"
                stackId="status"
                fill={STATUS_COLORS.done}
                name="COMPLETED"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ProjectsOverview;
